import { useState, useCallback } from 'react'
import styles from './style.module.scss'
import { PageTitle, Table } from '@Root/components'
import { generateStyleConfig } from '@Root/pages/Home/Admin/lib'
import { API } from '@Root/API'
import { useDispatch } from 'react-redux'
import { snackbarActions } from '@Root/store'
import { errorMessage } from '@Root/helpers'
import { CompareContactData } from './CompareContactData'

export const UpdatesPage = () => {
  const dispatch = useDispatch()
  const styleConfig = generateStyleConfig(true, true, true)
  const [reviewItem, setReviewItem] = useState(null)
  const [shouldBeUpdated, setShouldBeUpdated] = useState(false)

  const columns = [
    {
      name: 'ID',
      field: 'id',
      searchInputType: 'textInput',
      cellType: 'link',
    },
    {
      name: 'Contact Name',
      field: 'contact_name',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Email',
      field: 'contact_email',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Status',
      field: 'status_label',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Submitted At',
      field: 'created_at',
      searchInputType: 'datePicker',
      cellType: 'text',
    },
    {
      name: 'Reviewed By',
      field: 'reviewed_by',
      searchInputType: 'textInput',
      cellType: 'text',
    },
    {
      name: 'Reviewed At',
      field: 'reviewed_at',
      searchInputType: 'datePicker',
      cellType: 'text',
    },
  ]

  const onError = useCallback(
    error => {
      dispatch(snackbarActions.setSnackbar({ text: errorMessage(error), isError: true }))
    },
    [dispatch]
  )

  const onSuccess = useCallback(
    message => {
      dispatch(snackbarActions.setSuccessNotification({ text: message }))
    },
    [dispatch]
  )

  const openReview = useCallback(
    async row => {
      try {
        const { data } = await API.contactUpdates.getContactUpdateById(row.id)
        const item = data.data || data
        setReviewItem(item)
      } catch (error) {
        onError(error)
      }
    },
    [onError]
  )

  const fetchDataHandler = useCallback(async () => {
    try {
      const { data } = await API.contactUpdates.getContactUpdates()

      const items = data.data || data
      if (Array.isArray(items)) {
        const processedData = items.map(row => ({
          ...row,
          permissions: {
            id: { edit: true },
            contact_name: { edit: true },
            contact_email: { edit: true },
            status_label: { edit: true },
            created_at: { edit: true },
            reviewed_by: { edit: true },
            reviewed_at: { edit: true },
          },
        }))

        return {
          data: {
            data: processedData,
            meta: {
              from: 1,
              to: processedData.length,
              per_page: processedData.length,
              last_page: 1,
              total: processedData.length,
            },
            columns: columns,
          },
        }
      }
    } catch (error) {
      onError(error)
    }
  }, [onError])

  const handleApprove = useCallback(
    async selectedData => {
      try {
        await API.contactUpdates.approveContactUpdate(reviewItem.id, { selected_data: selectedData })
        onSuccess('Contact update approved successfully')
        setReviewItem(null)
        setShouldBeUpdated(prev => !prev)
      } catch (error) {
        onError(error)
      }
    },
    [reviewItem, onSuccess, onError]
  )

  const handleReject = useCallback(
    async notes => {
      try {
        await API.contactUpdates.rejectContactUpdate(reviewItem.id, { notes })
        onSuccess('Contact update rejected successfully')
        setReviewItem(null)
        setShouldBeUpdated(prev => !prev)
      } catch (error) {
        onError(error)
      }
    },
    [reviewItem, onSuccess, onError]
  )

  return (
    <div className={styles.wrapper}>
      <PageTitle title='Contact Updates' style={styleConfig.pageTitle} />
      <Table
        name='contact-updates'
        columns={columns}
        fetchDataHandler={fetchDataHandler}
        fetchColumnOptions={() => API.getColumnOptions('contact-updates')}
        fetchSaveColumnOptions={options => API.saveColumnOptions('contact-updates', options)}
        hasSorting={false}
        hasFilters={false}
        errorHandler={onError}
        successHandler={onSuccess}
        shouldBeUpdated={shouldBeUpdated}
        clickLinkHandlers={{ id: openReview }}
        styleConfig={{
          datePeriodDropdownStyle: {
            display: 'none',
          },
        }}
      />
      {reviewItem && (
        <CompareContactData
          item={reviewItem}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={() => setReviewItem(null)}
        />
      )}
    </div>
  )
}
